import React from "react";
import { Card } from "../common/Card";
import { TrendingUp, TrendingDown } from "lucide-react";

export interface StatsCardProps {
  title: string;
  value: string | number;
  icon?: React.ReactNode;
  color?:
    | "primary"
    | "success"
    | "warning"
    | "danger"
    | "accent"
    | "info"
    | "default";
  subtitle?: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
}

const colorClasses = {
  primary: {
    gradient: "from-primary-500/20 to-primary-600/20",
    border: "border-primary-500/30",
    icon: "bg-primary-500/20 text-primary-400",
    value: "text-primary-400",
  },
  success: {
    gradient: "from-emerald-500/20 to-emerald-600/20",
    border: "border-emerald-500/30",
    icon: "bg-emerald-500/20 text-emerald-400",
    value: "text-emerald-400",
  },
  warning: {
    gradient: "from-amber-500/20 to-amber-600/20",
    border: "border-amber-500/30",
    icon: "bg-amber-500/20 text-amber-400",
    value: "text-amber-400",
  },
  danger: {
    gradient: "from-red-500/20 to-red-600/20",
    border: "border-red-500/30",
    icon: "bg-red-500/20 text-red-400",
    value: "text-red-400",
  },
  accent: {
    gradient: "from-accent-500/20 to-accent-600/20",
    border: "border-accent-500/30",
    icon: "bg-accent-500/20 text-accent-400",
    value: "text-accent-400",
  },
  info: {
    gradient: "from-blue-500/20 to-blue-600/20",
    border: "border-blue-500/30",
    icon: "bg-blue-500/20 text-blue-400",
    value: "text-blue-400",
  },
  default: {
    gradient: "from-slate-500/20 to-slate-600/20",
    border: "border-slate-500/30",
    icon: "bg-slate-500/20 text-slate-400",
    value: "text-slate-300",
  },
};

export const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  icon,
  color = "primary",
  subtitle,
  trend,
}) => {
  const colors = colorClasses[color];

  return (
    <Card
      padding="none"
      className={`bg-gradient-to-br ${colors.gradient} ${colors.border} border backdrop-blur-sm hover:scale-[1.02] transition-transform`}
    >
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-slate-400 text-sm font-medium truncate">{title}</p>
            <p className={`text-2xl font-bold mt-1 ${colors.value}`}>{value}</p>
            {subtitle && (
              <p className="text-slate-500 text-xs mt-1">{subtitle}</p>
            )}
            {trend && (
              <div className="flex items-center mt-2 gap-1">
                {trend.isPositive ? (
                  <TrendingUp className="w-3 h-3 text-emerald-400" />
                ) : (
                  <TrendingDown className="w-3 h-3 text-red-400" />
                )}
                <span
                  className={`text-xs font-medium ${
                    trend.isPositive ? "text-emerald-400" : "text-red-400"
                  }`}
                >
                  {Math.abs(trend.value)}%
                </span>
                <span className="text-slate-500 text-xs">vs last period</span>
              </div>
            )}
          </div>
          {icon && (
            <div
              className={`w-10 h-10 rounded-xl flex items-center justify-center ${colors.icon}`}
            >
              {icon}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};
